import React, { useState, useRef } from 'react'
import { Button, Modal, Form, Select, Input, message } from 'antd'
import SignatureCanvas from 'react-signature-canvas'
import { useDangerModel } from '../dangerModel'
import useGlobalModel from '@/model/globalModel'
import { getBaseImgLookUrl, getToken, oldGetBaseRequestUrl } from '@/utils/constants'
import { dangerService } from '../dangerService'
import { maintenanceTypeList } from '@/types/AppEnums'

const { Item } = Form

export const AssignTechnicians: React.FC = () => {
    const [form] = Form.useForm()
    const [signature, setSignature] = useState('')
    const [loading, setLoading] = useState(false)
    const {
        user: { accountName },
    } = useGlobalModel(({ user }) => [user])
    const sigCanvas = useRef<SignatureCanvas>(null) // 创建一个引用 SignatureCanvas 的 ref
    const { isModalVisible, setIsModalVisible, maintenanceId, loadData, dangerTab } = useDangerModel()
    const { maintenanceTechnicianData } = useGlobalModel(({ user }) => [user])

    const isInspectionTab = dangerTab === 'inspection'

    const handleOk = async () => {
        try {
            const values = await form.validateFields()
            if (!signature) {
                message.warning('请签名！')
                return
            }
            // 检查技师信息
            if (!values.technicianId || !values.technicianName) {
                message.warning('请完善维修技师！')
                return
            }

            if (loading) {
                return
            }

            setLoading(true) // 开始加载状态

            // 处理签名和选中的技师信息
            const assignData = {
                id: maintenanceId,
                technicianId: values.technicianId,
                technicianName: values.technicianName,
                maintenanceType: values.maintenanceType,
                company: maintenanceTechnicianData.find((item) => item.value === values.technicianId)?.company,
                signMd5: signature,
                accountName,
                signName: '电脑签名(分配技师)',
            }
            const result = isInspectionTab
                ? await dangerService.inspectionAssignMaintainer(assignData)
                : await dangerService.assignMaintenance(assignData)

            if (result.success) {
                setIsModalVisible(false)
                form.resetFields()
                setSignature('')
                sigCanvas.current?.clear() // 清除画布
                message.success('分配成功！')
                await loadData()
            } else {
                message.error('分配失败: ' + (result.retMsg || '未知错误'))
            }
        } catch (error) {
            message.error('分配失败: 网络错误或系统异常')
        } finally {
            setLoading(false) // 结束加载状态
        }
    }

    const handleCancel = () => {
        setIsModalVisible(false)
        setSignature('')
        sigCanvas.current?.clear() // 清除画布
    }

    const dataURLToBlob = (dataURL: string) => {
        const arr: any = dataURL.split(',')
        const mime = arr[0].match(/:(.*?);/)[1]
        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
        }
        return new Blob([u8arr], { type: mime })
    }
    const uploadSignature = async (val: any) => {
        const formData = new FormData()
        formData.append('appCode', 'assets')
        formData.append('fileMode', '0')
        formData.append('file', val, '电脑签名.png') // 你可能需要根据实际情况修改文件名和扩展名

        try {
            const response = await fetch(`${oldGetBaseRequestUrl()}/fsrv/upload_file?Authorization=${getToken()}`, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `${getToken()}`, // 如果需要身份验证的话
                },
            })

            // 检查响应状态
            if (!response.ok) {
                message.error('签名上传失败')
                return null
            }

            // 解析 JSON 数据
            const data = await response.json()

            // 检查数据是否包含 retData 属性
            if (data && data.retData) {
                return data.retData // 返回 retData 或者根据你的需要进行处理
            } else {
                message.error('签名上传失败')
                return null
            }
        } catch (error) {
            console.error('Error uploading signature:', error)
            message.error('签名上传异常')
            return null
        }
    }
    const onSignatureChange = async () => {
        try {
            const dataURL = sigCanvas.current?.toDataURL() || ''
            if (dataURL) {
                const blob = dataURLToBlob(dataURL)
                const response = await uploadSignature(blob)
                if (response) {
                    setSignature(response)
                }
            }
        } catch (error) {
            console.error('Error uploading signature:', error)
        }
    }

    const clearSignature = () => {
        setSignature('')
        sigCanvas.current?.clear() // 通过 ref 清除画布
    }

    return (
        <Modal title="分配维修技师并签字确认" open={isModalVisible} onOk={handleOk} onCancel={handleCancel} okButtonProps={{ loading }}>
            <Form form={form} layout="vertical">
                <Item label="维修类型" name="maintenanceType" rules={[{ required: true, message: '请选择维修类型!' }]}>
                    <Select placeholder="请选择维修类型" options={maintenanceTypeList} />
                </Item>
                <Item label="选择技师" required rules={[{ required: true, message: '请选择维修技师!' }]}>
                    <Select
                        allowClear
                        placeholder="选择技师"
                        options={maintenanceTechnicianData.concat({ label: '分配给自己', value: 'no' })}
                        onChange={(value: any) => {
                            if (value === 'no') {
                                message.error('不可在电脑端：分配给自己，请到手机上传操作！').then(() => {})
                                // 清空选择
                                form.setFieldsValue({
                                    technicianId: undefined,
                                    technicianName: undefined,
                                })
                            } else {
                                // 设置技师ID和名称
                                const selectedOption = maintenanceTechnicianData.find((item) => item.value === value)
                                form.setFieldsValue({
                                    technicianId: value,
                                    technicianName: selectedOption ? selectedOption.label : '',
                                })
                            }
                        }}
                    />
                    <Form.Item name="technicianId" noStyle>
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name="technicianName" noStyle>
                        <Input type="hidden" />
                    </Form.Item>
                </Item>
                <Item label="签字确认">
                    <div style={{ border: '1px dashed' }}>
                        <SignatureCanvas
                            ref={sigCanvas} // 将 ref 绑定到 SignatureCanvas 实例
                            penColor="black"
                            backgroundColor="white"
                            canvasProps={{ width: 400, height: 200 }}
                            onEnd={onSignatureChange}
                        />
                    </div>
                    <Button type="primary" style={{ marginTop: '10px' }} onClick={clearSignature}>
                        清除签字
                    </Button>
                </Item>
                {signature && (
                    <Item label="签字预览">
                        <img src={getBaseImgLookUrl(signature)} style={{ maxWidth: '50px', maxHeight: '70px' }} alt="签字预览" />
                    </Item>
                )}
            </Form>
        </Modal>
    )
}
